import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatDateTime, formatTime } from "@/lib/helpers";
import { Phone, MapPin, ChevronDown, Car, Navigation, AlarmClockCheck, Shuffle } from "lucide-react";

export const navigationUrl = (appt) => {
  const dest = appt.student_navigation_address || appt.effective_meeting_location;
  if (!dest) return null;
  return `https://maps.google.com/?q=${encodeURIComponent(dest)}`;
};

// Instructor Dashboard -> single lesson card (used for both "Today's
// Schedule" and "Upcoming Lessons"). Extracted from InstructorDashboard.js
// (props-driven, no behavior change).
export const LessonCard = ({ appt, showActions, onComplete, onMarkStatus, onRunningLate }) => (
  <div className="border rounded-[var(--radius)] p-4 space-y-3" data-testid="instructor-lesson-card">
    <div className="flex items-start justify-between gap-2">
      <div>
        <p className="font-semibold text-sm">{appt.student_name || "Lesson"}</p>
        <p className="text-xs text-muted-foreground">{formatDateTime(appt.start_time)} - {formatTime(appt.end_time)}</p>
      </div>
      <StatusBadge status={appt.status} />
    </div>
    {appt.effective_meeting_location && (
      <div className="flex items-center gap-1.5 flex-wrap">
        <Badge variant="outline" className="inline-flex items-center gap-1 text-xs" data-testid="lesson-meeting-location-chip">
          <MapPin className="h-3 w-3" /> {appt.effective_meeting_location}
        </Badge>
        {appt.location_overridden && (
          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-800 gap-1 text-xs" data-testid="lesson-location-override-indicator" title="Meeting location changed for this lesson only">
            <Shuffle className="h-3 w-3" /> Overridden
          </Badge>
        )}
      </div>
    )}
    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
      {appt.student_phone && (
        <a href={`tel:${appt.student_phone}`} className="flex items-center gap-1 hover:text-foreground" data-testid="instructor-lesson-call-student">
          <Phone className="h-3.5 w-3.5" /> {appt.student_phone}
        </a>
      )}
      {navigationUrl(appt) && (
        <a href={navigationUrl(appt)} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-foreground" data-testid="instructor-lesson-navigate">
          <Navigation className="h-3.5 w-3.5" /> Navigate
        </a>
      )}
      {appt.vehicle_name && (
        <span className="flex items-center gap-1">
          <Car className="h-3.5 w-3.5" /> {appt.vehicle_name}
        </span>
      )}
    </div>
    {appt.student_emergency_contact_name && (
      <p className="text-xs text-muted-foreground">
        Emergency contact: {appt.student_emergency_contact_name} ({appt.student_emergency_contact_phone})
      </p>
    )}
    {showActions && (
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        <Button size="sm" onClick={() => onComplete(appt)} data-testid="instructor-lesson-complete-button">
          Mark Complete
        </Button>
        <Button size="sm" variant="outline" onClick={() => onRunningLate(appt)} data-testid="instructor-lesson-running-late-button">
          <AlarmClockCheck className="h-3.5 w-3.5 mr-1" /> Running Late
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" data-testid="instructor-lesson-status-menu">
              Other status <ChevronDown className="h-3.5 w-3.5 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => onMarkStatus(appt.id, "no_show")} data-testid="instructor-lesson-no-show-button">
              Student No Show
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onMarkStatus(appt.id, "rescheduled")} data-testid="instructor-lesson-rescheduled-button">
              Rescheduled
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onMarkStatus(appt.id, "cancelled")} data-testid="instructor-lesson-cancelled-button">
              Cancelled
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )}
  </div>
);
